import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Sparkles } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { CtaPanel, PageHero } from "@/components/site/Sections";
import { LeadMagnet } from "@/components/site/LeadMagnet";
import { StartupPricingCallout } from "@/components/site/StartupPricingCallout";

interface IndustryCardItem {
  name: string;
  note: string;
  href: string;
  featured?: boolean;
  badge?: string;
  solutionLabel?: string;
}

const featuredIndustries: IndustryCardItem[] = [
  {
    name: "Startups (Pre-seed to Series A)",
    note: "Clean books from day one, monthly close by the 5th, board-ready decks, and burn/runway visibility.",
    href: "/industries/startups",
    featured: true,
    badge: "Featured Dedicated Hub",
    solutionLabel: "Startup",
  },
  {
    name: "SaaS & Technology",
    note: "ASC 606 revenue recognition, Stripe/merchant sync, deferred revenue waterfalls, and SaaS metrics (CAC, LTV, NRR).",
    href: "/industries/saas",
    badge: "Dedicated Route",
    solutionLabel: "SaaS",
  },
  {
    name: "E-commerce & DTC",
    note: "Multi-channel reconciliation (Shopify, Amazon), FIFO landed cost COGS, and 20+ state sales tax nexus tracking.",
    href: "/industries/ecommerce",
    badge: "Dedicated Route",
    solutionLabel: "E-Commerce",
  },
  {
    name: "Agencies & Consultancies",
    note: "Project profitability, retainer revenue recognition, pass-through expenses, and 1099 contractor filings.",
    href: "/industries/professional-services",
    badge: "Dedicated Route",
    solutionLabel: "Agency & Consultancy",
  },
  {
    name: "India→US Cross-Border",
    note: "Delaware C-Corp parent + Indian dev subsidiary: transfer pricing documentation, Form 5472, and FEMA/RBI alignment.",
    href: "/industries/cross-border-india-us",
    badge: "Dedicated Route",
    solutionLabel: "Cross-Border",
  },
  {
    name: "Nonprofits & Research",
    note: "Fund accounting, federal grant budget tracking, Statement of Functional Expenses, and Uniform Guidance Single Audits.",
    href: "/industries/nonprofits",
    badge: "Dedicated Route",
    solutionLabel: "Nonprofit",
  },
];

const secondaryIndustries: IndustryCardItem[] = [
  {
    name: "Healthcare & Clinics",
    note: "Practice management billing cycles, HIPAA compliance-sensitive records, and multi-location reconciliations.",
    href: "/contact?industry=Healthcare",
    solutionLabel: "Healthcare",
  },
  {
    name: "Construction & Contracting",
    note: "Job costing, WIP schedules, retainage tracking, and prevailing wage subcontractor compliance.",
    href: "/contact?industry=Construction",
    solutionLabel: "Construction",
  },
  {
    name: "Real Estate & Property",
    note: "Property-level P&Ls, tenant security deposit escrow accounting, and investor distribution reporting.",
    href: "/contact?industry=Real+Estate",
    solutionLabel: "Real Estate",
  },
  {
    name: "Manufacturing & Distribution",
    note: "Cost accounting, Bill of Materials (BOM), standard costing variances, and inventory valuation.",
    href: "/contact?industry=Manufacturing",
    solutionLabel: "Manufacturing",
  },
  {
    name: "Retail & Multi-Location",
    note: "POS clearing account reconciliation, cash drawer audits, and multi-store gross margin reporting.",
    href: "/contact?industry=Retail",
    solutionLabel: "Retail",
  },
  {
    name: "Bootstrapped SMBs",
    note: "Complete outsourced finance back-office at a fraction of an in-house accounting hire.",
    href: "/contact?industry=SMB",
    solutionLabel: "Bootstrapped SMBs",
  },
];

export const Route = createFileRoute("/industries/")({
  head: () => ({
    meta: [
      { title: "Industries We Serve | Finendra" },
      {
        name: "description",
        content:
          "Industry-tailored finance operations for Startups, SaaS, E-commerce, Agencies, Nonprofits, and India-US Cross-Border businesses.",
      },
      { property: "og:title", content: "Industries We Serve | Finendra" },
      {
        property: "og:description",
        content: "Industry-specific accounting expertise tailored to your business model.",
      },
      { property: "og:url", content: "https://finendra.com/industries" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Industries We Serve | Finendra" },
    ],
    links: [{ rel: "canonical", href: "https://finendra.com/industries" }],
  }),
  component: IndustriesPage,
});

function IndustriesPage() {
  return (
    <>
      <PageHero
        eyebrow="Industries"
        title="Accounting shaped around how your business actually operates."
        body="Job costing looks nothing like SaaS deferred revenue. We staff each engagement with finance professionals who already understand your sector's metrics, workflows, and compliance."
      />

      {/* Featured Primary Sectors */}
      <section className="px-6 pb-16">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              Core Verticals
            </span>
            <h2 className="mt-2 font-display text-2xl font-bold text-brand-navy">
              Specialized Industry Practices
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {featuredIndustries.map((ind, i) => (
              <Reveal key={ind.name} delay={(i % 3) * 0.06} className="h-full">
                <Link
                  to={ind.href}
                  className={`group flex h-full flex-col justify-between rounded-3xl border p-8 transition-[border-color,background-color,box-shadow,transform] duration-200 ease-out hover:-translate-y-1 hover:shadow-xl active:scale-[0.985] active:duration-75 motion-reduce:transform-none select-none ${
                    ind.featured
                      ? "border-brand-accent bg-gradient-to-br from-brand-accent/5 to-background shadow-lg ring-2 ring-brand-accent/20"
                      : "border-brand-navy/10 bg-background hover:border-brand-accent/40 hover:bg-brand-surface/40"
                  }`}
                >
                  <div>
                    <div className="flex items-center justify-between">
                      <span className="font-display text-sm font-bold text-brand-accent">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {ind.badge && (
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider ${
                            ind.featured
                              ? "bg-brand-accent text-background"
                              : "bg-brand-surface text-brand-navy/70 border border-brand-navy/10"
                          }`}
                        >
                          {ind.badge}
                        </span>
                      )}
                    </div>

                    <h3 className="mt-5 font-display text-xl font-bold text-brand-navy group-hover:text-brand-accent transition-colors flex items-center gap-1.5">
                      {ind.name}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-brand-navy/70">{ind.note}</p>
                  </div>

                  <div className="mt-6 flex items-center gap-2 text-xs font-bold text-brand-accent group-hover:underline pt-4 border-t border-brand-navy/5">
                    View {ind.solutionLabel ?? ind.name.split(" ")[0]} Solutions{" "}
                    <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-1" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Secondary Industries */}
      <section className="px-6 py-16 bg-brand-surface/50 border-t border-brand-navy/5">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              Additional Sectors
            </span>
            <h2 className="mt-2 font-display text-2xl font-bold text-brand-navy">
              More Industries We Support
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {secondaryIndustries.map((ind, i) => (
              <Reveal key={ind.name} delay={(i % 3) * 0.05}>
                <Link
                  to="/contact"
                  search={{ industry: ind.name }}
                  className="group block h-full rounded-2xl border border-brand-navy/10 bg-background p-6 transition-[border-color,box-shadow,transform] duration-200 ease-out hover:-translate-y-0.5 hover:border-brand-accent/30 hover:shadow-md active:scale-[0.985] active:duration-75 motion-reduce:transform-none select-none"
                >
                  <h3 className="font-display text-lg font-bold text-brand-navy group-hover:text-brand-accent transition-colors">
                    {ind.name}
                  </h3>
                  <p className="mt-2 text-xs leading-relaxed text-brand-navy/60">{ind.note}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-accent">
                    Inquire about {ind.solutionLabel ?? ind.name} <ArrowRight className="size-3" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Startup Pricing Callout */}
      <StartupPricingCallout slug="general" />

      {/* Lead Magnet */}
      <section className="px-6 py-12">
        <LeadMagnet defaultIndustry="General Business" />
      </section>

      <CtaPanel
        title="Don't see your exact industry?"
        body="We work with growing businesses of every shape worldwide. Tell us what you do and we'll tell you exactly how we'd support your books."
      />
    </>
  );
}
